import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { deletionPrompt, type DeletionReport } from '@fsm/shared';
import { Button, Modal } from './ui';

/**
 * Ticking rows, and doing something to the ticked ones.
 *
 * Written once because four lists need it and four implementations of
 * "select all" is four chances to get the half-checked state wrong.
 *
 * The rule that shapes it: a selection belongs to the rows currently on
 * screen. Filter the list, search it, change a tab — and anything selected
 * that is no longer visible is dropped. Without that, somebody searches for
 * "Whitlock", ticks four rows, clears the search and deletes, and loses four
 * records they can no longer see. The selection is pruned against the visible
 * ids on every render, so what the bar says is always what is in front of
 * them.
 */
export function useSelection<T extends { id: string }>(rows: T[]) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());

  const visible = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);

  /* Only what is still on screen. See the note above. */
  const selected = useMemo(
    () => [...picked].filter((id) => visible.has(id)),
    [picked, visible],
  );

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  return {
    selected,
    count: selected.length,
    isSelected: (id: string) => selectedSet.has(id),
    toggle: (id: string) =>
      setPicked((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    /** The header tick: all on, unless all are already on. */
    allVisible: rows.length > 0 && selected.length === rows.length,
    someVisible: selected.length > 0 && selected.length < rows.length,
    toggleAll: () =>
      setPicked((prev) => {
        const next = new Set(prev);
        if (selected.length === rows.length) for (const r of rows) next.delete(r.id);
        else for (const r of rows) next.add(r.id);
        return next;
      }),
    clear: () => setPicked(new Set()),
  };
}

export type Selection = ReturnType<typeof useSelection>;

/**
 * The tick itself.
 *
 * `stopPropagation` because every one of these lists navigates on row click,
 * and a tick that opens the record is a tick nobody can use.
 */
export function SelectCell({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      aria-label={label}
      ref={(el) => {
        if (el) el.indeterminate = Boolean(indeterminate) && !checked;
      }}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation();
        onChange();
      }}
      className="size-4 cursor-pointer accent-accent align-middle"
    />
  );
}

/**
 * The bar that appears once something is ticked.
 *
 * Sits above the table rather than floating over it: a floating bar covers
 * the last row, which on a short list is the row somebody is looking at.
 */
export function SelectionBar({
  count,
  one,
  many,
  onClear,
  onDelete,
  busy,
}: {
  count: number;
  one: string;
  many: string;
  onClear: () => void;
  onDelete: () => void;
  busy?: boolean;
}) {
  if (count === 0) return null;
  return (
    <div className="flex items-center gap-3 border-b border-line bg-accent-soft px-[21px] py-2.5">
      <span className="text-[13px] font-semibold text-ink">
        {count} {count === 1 ? one : many} selected
      </span>
      <Button size="sm" icon="trash" variant="danger" loading={busy} onClick={onDelete}>
        Delete
      </Button>
      <button
        type="button"
        onClick={onClear}
        className="text-[12.5px] font-medium text-ink-3 hover:text-ink"
      >
        Clear
      </button>
    </div>
  );
}

/**
 * Confirm, then say what happened.
 *
 * One modal with two faces rather than a confirm and a separate toast. A
 * partial result is the normal outcome here — some rows go, some are refused
 * because they carry invoices — and a toast that says "3 deleted" while
 * saying nothing about the other four is how somebody comes to believe a
 * record is gone when it is not.
 *
 * The refusals stay on screen until dismissed, each naming its record and its
 * reason, because the reason is a next action: void this payment, cancel that
 * job.
 */
export function ConfirmDeleteModal({
  open,
  onClose,
  count,
  one,
  many,
  warning,
  onConfirm,
  onDeleted,
}: {
  open: boolean;
  onClose: () => void;
  count: number;
  one: string;
  many: string;
  /** What else goes with it — line items, photos, notes. */
  warning: string;
  onConfirm: () => Promise<DeletionReport>;
  /** Called once something actually went, so the page can refetch. */
  onDeleted: () => void;
}) {
  const [report, setReport] = useState<DeletionReport | null>(null);

  const run = useMutation({
    mutationFn: onConfirm,
    onSuccess: (result) => {
      setReport(result);
      if (result.deleted > 0) onDeleted();
      /* Nothing refused means nothing left to read: close on the way out. */
      if (result.refused === 0) close();
    },
  });

  function close() {
    setReport(null);
    run.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={report ? 'Not everything could be deleted' : deletionPrompt(count, one, many)}
      footer={
        report ? (
          <Button variant="primary" onClick={close}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="danger"
              icon="trash"
              loading={run.isPending}
              onClick={() => run.mutate()}
            >
              Delete {count === 1 ? one : `${count} ${many}`}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-3">
        {report ? (
          <>
            {report.deleted > 0 && (
              <p className="text-[13px] text-ink-2">
                {report.deleted} {report.deleted === 1 ? one : many} deleted.{' '}
                {report.refused} could not be.
              </p>
            )}
            <ul className="space-y-2">
              {report.refusals.map((r) => (
                <li
                  key={r.id}
                  className="rounded-lg bg-danger-soft px-3 py-2 text-[12.5px] leading-relaxed text-ink-2"
                >
                  <span className="font-semibold text-ink">{r.name}</span> — {r.message}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <p className="text-[13px] leading-relaxed text-ink-2">{warning}</p>
            <p className="text-[12.5px] leading-relaxed text-ink-3">
              This cannot be undone.
            </p>
            {run.isError && (
              <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">
                {(run.error as { message?: string }).message ?? 'That could not be deleted'}
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * Deleting the one record you are looking at.
 *
 * A quiet strip under the page rather than a button in the heading. Delete is
 * not something anybody arrives wanting to do, and putting it beside the
 * actions people use all day is how a misclick becomes a lost invoice. Down
 * here it takes a deliberate scroll, which is the right amount of friction
 * for an action with no undo.
 *
 * Shares its modal with the bulk path, so the confirmation, the wording and
 * the refusal all read the same whether one record or two hundred.
 */
export function DeleteRecord({
  one,
  many,
  warning,
  onConfirm,
  onDeleted,
}: {
  one: string;
  many: string;
  warning: string;
  onConfirm: () => Promise<DeletionReport>;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <>
      <div className="mt-6 flex items-center justify-between gap-4 rounded-[8px] border border-line px-4 py-3">
        <p className="text-[12.5px] leading-relaxed text-ink-3">
          Deleting this {one} cannot be undone.
        </p>
        <Button size="sm" variant="danger" icon="trash" onClick={() => setConfirming(true)}>
          Delete {one}
        </Button>
      </div>

      <ConfirmDeleteModal
        open={confirming}
        onClose={() => setConfirming(false)}
        count={1}
        one={one}
        many={many}
        warning={warning}
        onConfirm={onConfirm}
        onDeleted={onDeleted}
      />
    </>
  );
}
